import { Formik, FormikHelpers } from 'formik';
import React, { useCallback } from 'react';
import { CompanyProfileInfo } from './CompanyProfileInfo';
import { ECrmCompanyType, ICrmCompanyProfile, IDirectoryEntry } from 'services/CrmApi/types/DirectoryEntry';
import { companyProfileFormValidationSchema, travelCompanyPublishedProfileFormValidationSchema, ICompanyProfileFormValues, supplierPublishedProfileFormValidationSchema, hotelPublishedProfileFormValidationSchema } from './companyProfileForm';
import { ErrorBar } from 'ui/NetworkStatusBar';
import { useUpdateCompanyMutation } from 'services/CrmApi/mutations/useUpdateCompanyMutation';
import { IUpdateCompanyRequest } from 'services/CrmApi/types/CrmCompanyTypes';
import { useNotifications } from 'hooks/useNotifications';
import { useQueryClient } from '@tanstack/react-query';
import { useDefaultOnErrorHandler } from 'hooks/useDefaultOnErrorHandler';
import { useCrmDirectoryEntry } from 'services/CrmApi/queries/useCrmDirectoryEntry';

interface ICrmCompanyEditProfileParams {
  isPublished: boolean;
  directoryEntry: IDirectoryEntry;
}

export const CrmCompanyEditProfile: React.FC<ICrmCompanyEditProfileParams> = ({ isPublished, directoryEntry }) => {
  const { showSuccessNotification } = useNotifications();
  const { defaultOnErrorHandler } = useDefaultOnErrorHandler();
  const profile = directoryEntry.profile as ICrmCompanyProfile;

  const queryClient = useQueryClient();
  const { crmDirectoryEntryResponse } = useCrmDirectoryEntry(directoryEntry.id);
  const updateCompanyMutation = useUpdateCompanyMutation();

  const validationMapping = {
    [ECrmCompanyType.TRAVEL_COMPANY]: travelCompanyPublishedProfileFormValidationSchema,
    [ECrmCompanyType.ACTIVITIES]: supplierPublishedProfileFormValidationSchema,
    [ECrmCompanyType.GROUND_SERVICES]: supplierPublishedProfileFormValidationSchema,
    [ECrmCompanyType.TRANSFERS]: supplierPublishedProfileFormValidationSchema,
    [ECrmCompanyType.HOTEL]: hotelPublishedProfileFormValidationSchema,
  };
  const validationSchema = validationMapping[profile.companyType.code]
  
  const handleFormSubmit = useCallback(async (values: ICompanyProfileFormValues, formikHelpers: FormikHelpers<ICompanyProfileFormValues>) => {
    const requestData : IUpdateCompanyRequest = {
      directoryId: directoryEntry.id,
      profile: {
        companyTypeCode: values.companyTypeCode,
        name: values.name,
        tcType: values.tcType,
        industry: values.industry,
        employeeCount: values.employeeCount.length > 0 ? Number(values.employeeCount) : null,
        website: values.website,
      },
      email: values.email.length > 0 ? values.email : null,
      countryCode: values.countryCode ?? null,
      acceptsEmail: values.acceptsEmail,
      mobile: values.mobile || null,
      address: values.address || null,
      linkedin: values.linkedin,
      twitter: values.twitter,
      instagram: values.instagram,
      currencyCode: values.currencyCode ?? null,
    }

    try {
      await updateCompanyMutation.mutateAsync(requestData);
      showSuccessNotification('Company updated.');
      queryClient.invalidateQueries({ queryKey: ['crm-directory-entry', directoryEntry.id] })
    } catch (error) {
      defaultOnErrorHandler(error, 'Failed to update a company');
      queryClient.invalidateQueries({ queryKey: ['crm-directory-entry', directoryEntry.id] })
    }
  }, [defaultOnErrorHandler, directoryEntry.id, queryClient, showSuccessNotification, updateCompanyMutation]);

  if (!('companyType' in profile)) {
    return (
      <div className="mt-[20px]">
        <ErrorBar message="It seems this is not a company profile" />
      </div>
    )
  }

  const initialValues: ICompanyProfileFormValues = {
      companyTypeCode: profile.companyType?.code ?? '',
      name: directoryEntry?.fullName ?? '',
      tcType: profile?.tcType,
      industry: profile?.industry ?? '',
      employeeCount: !profile.employeeCount ? '' : String(profile.employeeCount),
      countryCode: directoryEntry?.country?.code ?? null,
      address: directoryEntry?.address ?? '',
      mobile: directoryEntry?.mobile ?? '',
      email: directoryEntry?.email ?? '',
      acceptsEmail: directoryEntry?.acceptsEmail ?? false,
      website: profile?.website ?? '',
      linkedin: directoryEntry?.linkedin ?? '',
      twitter: directoryEntry?.twitter ?? '',
      instagram: directoryEntry?.instagram ?? '',
      currencyCode: directoryEntry?.currency?.code ?? null,
  };
  
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={isPublished ? validationSchema : companyProfileFormValidationSchema}
      onSubmit={handleFormSubmit}
    >
      <CompanyProfileInfo
        isSaving={updateCompanyMutation.isPending || crmDirectoryEntryResponse.isFetching} 
        isPublished={isPublished} 
      />
    </Formik>
  )
};
